import React, { useState, useEffect } from 'react';
import {
  Modal,
  Form,
  Row,
  Col,
  Select,
  Input,
  InputNumber,
  Button,
  Space,
  message,
  Badge,
  Typography,
  Alert,
  Progress
} from 'antd';
import {
  DeleteOutlined,
  FileOutlined,
  UploadOutlined,
  LoadingOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';
import api from '../../../utils/axios';

const { Text } = Typography;
const { Option } = Select;

// Options statiques
const usineOptions = [
  'CIMAF',  
  'SOCOCIM', 
  'CIMENCAM', 
  'Autre'
];

const AddAchatModal = ({ visible, editingAchat, onCancel, onSuccess }) => {
  const [form] = Form.useForm();
  const [uploadedFile, setUploadedFile] = useState(null);
  const [fileName, setFileName] = useState('');
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [API_URL] = useState(process.env.REACT_APP_API_URL || 'http://localhost:8000');

  // Réinitialiser les états quand le modal s'ouvre
  useEffect(() => {
    if (visible) {
      setUploadedFile(null);
      setFileName('');
      setUploadProgress(0);
      setUploading(false);
      
      if (editingAchat) {
        form.setFieldsValue({
          ...editingAchat,
          quantite: editingAchat.quantite || 0,
          prixParTonne: editingAchat.prixParTonne || 0,
          date: editingAchat.date ? dayjs(editingAchat.date).format('YYYY-MM-DD') : dayjs().format('YYYY-MM-DD')
        });
        
        // Si l'achat a déjà un fichier
        if (editingAchat.fichierVersement) {
          setFileName(editingAchat.nomFichier || 'Document joint');
          setUploadedFile(editingAchat.fichierVersement);
        }
      } else {
        form.resetFields();
        form.setFieldsValue({
          date: dayjs().format('YYYY-MM-DD'),
          nomUsine: 'CIMAF'
        });
      }
    }
  }, [visible, editingAchat, form, API_URL]);

  // Formater les montants en FCFA
  const formatMontant = (montant) => {
    const montantNum = parseFloat(montant) || 0;
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'XOF',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(montantNum);
  };

  // Calcul automatique du total
  const calculateTotalInForm = () => {
    const quantite = parseFloat(form.getFieldValue('quantite')) || 0;
    const prixParTonne = parseFloat(form.getFieldValue('prixParTonne')) || 0;
    const total = Math.round(quantite * prixParTonne);
    
    form.setFieldsValue({ 
      totalCalculé: total,
      totalCalculéFormatted: formatMontant(total)
    });
    
    return total;
  };

  // Fonction de compression d'image
  const compressImage = (file, maxWidth, maxHeight, quality) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = (event) => {
        const img = new Image();
        img.src = event.target.result;
        img.onload = () => {
          const canvas = document.createElement('canvas');
          let width = img.width;
          let height = img.height;
          
          // Redimensionner si nécessaire
          if (width > maxWidth) {
            height = (height * maxWidth) / width;
            width = maxWidth;
          }
          
          if (height > maxHeight) {
            width = (width * maxHeight) / height;
            height = maxHeight;
          }
          
          canvas.width = width;
          canvas.height = height;
          
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, width, height);
          
          const compressedBase64 = canvas.toDataURL('image/jpeg', quality);
          resolve(compressedBase64);
        };
        img.onerror = reject;
      };
      reader.onerror = reject;
    });
  };

  // Gestion de l'upload de fichier selon votre méthode
  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Vérifier la taille du fichier (max 10MB)
    const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10 MB
    if (file.size > MAX_FILE_SIZE) {
      message.error(`Le fichier est trop volumineux (${(file.size / (1024 * 1024)).toFixed(2)} MB). Maximum: 10MB`);
      event.target.value = '';
      return;
    }

    // Vérifier le type de fichier
    const allowedTypes = [
      'image/jpeg',
      'image/jpg',
      'image/png',
      'image/gif',
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    ];

    if (!allowedTypes.includes(file.type)) {
      message.error('Type de fichier non supporté. Formats acceptés : JPG, PNG, GIF, PDF, Word, Excel');
      event.target.value = '';
      return;
    }

    setFileName(file.name);

    try {
      setUploading(true);
      setUploadProgress(0);
      
      // Simuler la progression
      const interval = setInterval(() => {
        setUploadProgress(prev => {
          const newProgress = prev + 10;
          if (newProgress >= 90) {
            clearInterval(interval);
            return 90;
          }
          return newProgress;
        });
      }, 100);

      let base64String;
      
      // Compresser les images seulement
      if (file.type.startsWith('image/') && file.type !== 'image/gif') {
        base64String = await compressImage(file, 1024, 1024, 0.7);
        message.success('Image compressée avec succès');
      } else {
        // Pour les autres types de fichiers
        const reader = new FileReader();
        base64String = await new Promise((resolve, reject) => {
          reader.onloadend = () => resolve(reader.result);
          reader.onerror = reject;
          reader.readAsDataURL(file);
        });
      }
      
      // Vérifier la taille finale (limite de 20 MB)
      if (base64String.length > 20 * 1024 * 1024) {
        message.error('Le fichier est encore trop important après traitement. Veuillez choisir un fichier plus petit.');
        setUploadedFile(null);
        setFileName('');
        event.target.value = '';
        setUploading(false);
        setUploadProgress(0);
        return;
      }
      
      clearInterval(interval);
      setUploadProgress(100);
      
      // Simuler un petit délai pour voir la progression complète
      setTimeout(() => {
        setUploadedFile(base64String);
        setUploading(false);
        setUploadProgress(0);
        message.success('Fichier préparé avec succès');
      }, 500);
      
    } catch (error) {
      console.error('Erreur lors du traitement du fichier:', error);
      message.error('Erreur lors du traitement du fichier');
      setUploadedFile(null);
      setFileName('');
      event.target.value = '';
      setUploading(false);
      setUploadProgress(0);
    }
  };

  // Supprimer le fichier
  const handleRemoveFile = () => {
    setUploadedFile(null);
    setFileName('');
    // Réinitialiser l'input file
    const fileInput = document.getElementById('documentUpload');
    if (fileInput) fileInput.value = '';
  };

  // Soumettre le formulaire
  const handleSubmit = async (values) => {
    setLoading(true);
    try {
      const quantite = parseFloat(values.quantite) || 0;
      const prixParTonne = parseFloat(values.prixParTonne) || 0;
      
      let fichierVersement = null;
      let nomFichier = null;

      // Si un fichier a été uploadé
      if (uploadedFile) {
        // Convertir base64 en fichier
        const base64Response = await fetch(uploadedFile);
        const blob = await base64Response.blob();
        
        // Créer un FormData pour l'upload
        const formData = new FormData();
        formData.append('file', blob, fileName);
        
        try {
          const uploadResponse = await api.post('/api/upload', formData, {
            headers: {
              'Content-Type': 'multipart/form-data'
            }
          });
          
          if (uploadResponse.data.success) {
            fichierVersement = uploadResponse.data.data.filename;
            nomFichier = uploadResponse.data.data.originalname || fileName;
          }
        } catch (uploadError) {
          console.error('Erreur upload fichier:', uploadError);
          message.warning('Fichier non uploadé, création sans fichier');
        }
      }

      // Préparer les données
      const achatData = {
        nomUsine: values.nomUsine,
        quantite: quantite,
        prixParTonne: prixParTonne,
        date: values.date,
        fichierVersement: fichierVersement || (editingAchat && !uploadedFile ? editingAchat.fichierVersement : null),
        nomFichier: nomFichier || (editingAchat && !uploadedFile ? editingAchat.nomFichier : null)
      };

      console.log('📤 Envoi données:', achatData);

      let response;
      if (editingAchat) {
        // Modification
        response = await api.put(`/api/achats/${editingAchat.id}`, achatData);
      } else {
        // Nouvel achat
        response = await api.post('/api/achats', achatData);
      }

      if (response.data.success) {
        message.success(editingAchat ? 'Achat modifié avec succès !' : 'Achat ajouté avec succès !');
        onSuccess();
        onCancel();
      } else {
        message.error(response.data.message || 'Erreur lors de l\'opération');
      }
    } catch (error) {
      console.error('❌ Erreur lors de la sauvegarde:', error);
      message.error('Erreur lors de la sauvegarde: ' + (error.response?.data?.message || error.message));
    } finally {
      setLoading(false);
    }
  };

  // Fermer modal
  const handleCancel = () => {
    form.resetFields();
    setUploadedFile(null);
    setFileName('');
    setUploadProgress(0);
    setUploading(false);
    onCancel();
  };

  return (
    <Modal
      title={editingAchat ? "Modifier l'Achat" : "Nouvel Achat"}
      open={visible}
      onCancel={handleCancel}
      footer={null}
      width={800}
      destroyOnClose
    >
      <Form
        form={form}
        layout="vertical"
        className="modern-form"
        onFinish={handleSubmit}
        onValuesChange={() => {
          calculateTotalInForm();
        }}
      >
        <Row gutter={24}>
          <Col span={12}>
            <Form.Item
              name="nomUsine"
              label="Nom Usine"
              rules={[{ required: true, message: 'Veuillez sélectionner l\'usine' }]}
            >
              <Select placeholder="Sélectionnez l'usine" showSearch>
                {usineOptions.map(usine => (
                  <Option key={usine} value={usine}>{usine}</Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="date"
              label="Date d'achat"
              rules={[{ required: true, message: 'La date est requise' }]}
            >
              <Input 
                type="date" 
                disabled={editingAchat}
              />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={24}>
          <Col span={12}>
            <Form.Item
              name="quantite"
              label="Quantité (tonnes)"
              rules={[
                { required: true, message: 'Veuillez entrer la quantité' },
                { 
                  type: 'number', 
                  min: 0.05, 
                //max: 100000000,
                  message: 'La quantité doit être entre 0.05 et 100000000 tonnes' 
                }
              ]}
            >
              <InputNumber
                min={0.05}
              //  max={100000000}
                step={0.05}
                style={{ width: '100%' }}
                placeholder="Ex: 50.00"
                formatter={value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ' ')}
                parser={value => value?.toString().replace(/\s/g, '')}
                addonAfter="t"
                onChange={() => calculateTotalInForm()}
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="prixParTonne"
              label="Prix par tonne (FCFA)"
              rules={[
                { required: true, message: 'Veuillez entrer le prix par tonne' },
                { type: 'number', min: 1, message: 'Le prix doit être positif' }
              ]}
            >
              <InputNumber
                min={1}
                style={{ width: '100%' }}
                placeholder="Ex: 110000"
                formatter={value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ' ')}
                parser={value => value?.toString().replace(/\s/g, '')}
                addonAfter="FCFA/t"
                onChange={() => calculateTotalInForm()}
              />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={24}>
          <Col span={12}>
            <Form.Item
              label="Total calculé (FCFA)"
            >
              <Input
                disabled
                value={form.getFieldValue('totalCalculéFormatted') || formatMontant(0)}
                style={{ 
                  fontWeight: 'bold', 
                  color: '#0a6b5a',
                  fontSize: '16px',
                  textAlign: 'right'
                }}
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="totalCalculé"
              hidden
            >
              <Input type="hidden" />
            </Form.Item>
            <Form.Item
              name="totalCalculéFormatted"
              hidden
            >
              <Input type="hidden" />
            </Form.Item>
          </Col>
        </Row>

        {/* SECTION DOCUMENT selon votre méthode */}
        <Row>
          <Col span={24}>
            <Form.Item
              label="Document (fichier à joindre)"
              required={false}
            >
              {!uploadedFile ? (
                <div className="border rounded p-4 text-center border-dashed hover:border-blue-500 transition-colors cursor-pointer">
                  <input
                    type="file"
                    id="documentUpload"
                    onChange={handleFileUpload}
                    accept=".jpg,.jpeg,.png,.gif,.pdf,.doc,.docx,.xls,.xlsx"
                    className="hidden"
                    disabled={uploading}
                  />
                  <label htmlFor="documentUpload" className="cursor-pointer block">
                    <div className="py-3">
                      {uploading ? (
                        <LoadingOutlined className="text-3xl text-blue-500 mb-3" />
                      ) : (
                        <UploadOutlined className="text-3xl text-gray-400 mb-3" />
                      )}
                      <p className="mt-2 mb-1 font-medium">
                        {uploading ? 'Traitement en cours...' : 'Cliquez pour télécharger un fichier'}
                      </p>
                      <p className="text-gray-500 text-sm">
                        ou glissez-déposez ici
                      </p>
                      <div className="mt-2 flex flex-wrap justify-center gap-2">
                        <Badge color="blue" text="JPG/PNG" />
                        <Badge color="green" text="PDF" />
                        <Badge color="orange" text="Word" />
                        <Badge color="purple" text="Excel" />
                      </div>
                      <p className="text-gray-500 text-sm mt-2">
                        <span className="text-gray-400">Taille maximale : 10MB • Les images sont automatiquement compressées</span>
                      </p>
                      
                      {uploading && uploadProgress > 0 && (
                        <div className="mt-4">
                          <Progress 
                            percent={uploadProgress} 
                            size="small" 
                            status="active" 
                            strokeColor={{
                              '0%': '#108ee9',
                              '100%': '#87d068',
                            }}
                          />
                        </div>
                      )}
                    </div>
                  </label>
                </div>
              ) : (
                <div className="border rounded p-3 bg-gray-50">
                  <div className="flex justify-between items-center">
                    <div className="flex items-center gap-3">
                      <FileOutlined className="text-xl text-blue-500" />
                      <div>
                        <p className="mb-0 font-bold">{fileName}</p>
                        <p className="mb-0 text-gray-500 text-sm">
                          Taille : {uploadedFile ? Math.round(uploadedFile.length / 1024) : 0} KB
                          {uploadedFile && uploadedFile.length > 5 * 1024 * 1024 && (
                            <span className="text-amber-500 ml-2">
                              <span className="mr-1">⚠</span>
                              Fichier volumineux
                            </span>
                          )}
                        </p>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        type="button"
                        danger
                        size="small"
                        onClick={handleRemoveFile}
                        icon={<DeleteOutlined />}
                        disabled={uploading}
                      >
                        Supprimer
                      </Button>
                    </div>
                  </div>
                </div>
              )}
              
              {uploading && (
                <Alert
                  message="Traitement du fichier en cours..."
                  type="info"
                  showIcon
                  className="mt-2"
                />
              )}
            </Form.Item>
          </Col>
        </Row>

        {editingAchat?.fichierVersement && !uploadedFile && (
          <Row>
            <Col span={24}>
              <Alert
                message={
                  <div>
                    <Text strong>Document actuel : </Text>
                    <a 
                      href={`${API_URL}/uploads/${editingAchat.fichierVersement}`} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="ml-2"
                    >
                      {editingAchat.nomFichier || 'Télécharger le document'}
                    </a>
                  </div>
                }
                description="Ce document sera remplacé si vous uploadez un nouveau fichier"
                type="info"
                showIcon
                className="mb-4"
              />
            </Col>
          </Row>
        )}

        <div style={{ textAlign: 'right', marginTop: '24px' }}>
          <Space>
            <Button onClick={handleCancel} size="large" disabled={uploading || loading}>
              Annuler
            </Button>
            <Button 
              type="primary" 
              htmlType="submit" 
              loading={loading} 
              className="btn-primary"
              size="large"
              disabled={uploading}
            >
              {editingAchat ? 'Modifier' : 'Ajouter'}
            </Button>
          </Space>
        </div>
      </Form>
    </Modal>
  );
};

export default AddAchatModal;